import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Modal, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import {
  useAccounts,
  AccountSelect,
  financeAuthPayload,
} from "../../components/finance/AccountSelect";
import FinanceBreadcrumb from "../../components/finance/Breadcrumb";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Accounts Payable
 *
 * The payment view of accrued liabilities. payables/list returns the same
 * liability rows; paying one posts a journal (Dr Liability, Cr Cash/Bank),
 * which is why the pay modal collects the cash/bank account to credit. A
 * partial payment moves the liability to partial_paid; paying the balance
 * moves it to paid.
 *
 * Backend dependency: ApiConfig.accountsPayableList + accountsPayablePay.
 * Row shape: { liability_id, liability_name, liability_description,
 *   liability_amount, liability_amount_paid, liability_due_date,
 *   liability_status }
 */
const AccountsPayable = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const { accounts } = useAccounts();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", search: "" });

  // Pay-down modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payAccountId, setPayAccountId] = useState("");
  const [paying, setPaying] = useState(false);

  const fetchPayables = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        ...financeAuthPayload(),
        status: filters.status,
        search: filters.search,
      };
      const res = await simplePostCall(
        ApiConfig.accountsPayableList,
        JSON.stringify(payload)
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Payables fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.search]);

  useEffect(() => {
    fetchPayables();
  }, [fetchPayables]);

  const balanceOf = (r) =>
    (parseFloat(r.liability_amount) || 0) -
    (parseFloat(r.liability_amount_paid) || 0);

  const totals = rows.reduce(
    (acc, r) => {
      const bal = balanceOf(r);
      acc.outstanding += bal;
      const due = r.liability_due_date ? new Date(r.liability_due_date) : null;
      if (due && !isNaN(due) && due < new Date() && bal > 0) {
        acc.overdue += bal;
      }
      return acc;
    },
    { outstanding: 0, overdue: 0 }
  );

  const openPayModal = (row) => {
    setActiveRow(row);
    setPayAmount(String(balanceOf(row)));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayAccountId("");
    setShowPayModal(true);
  };

  const submitPayment = async () => {
    if (!activeRow || !payAmount || Number(payAmount) <= 0) {
      toast.warning("Enter a payment amount greater than zero.");
      return;
    }
    if (!payAccountId) {
      toast.warning("Select the cash / bank account the payment is made from.");
      return;
    }
    setPaying(true);
    try {
      const res = await simplePostCall(
        ApiConfig.accountsPayablePay,
        JSON.stringify({
          ...financeAuthPayload(),
          liability_id: activeRow.liability_id,
          payment_date: payDate,
          payment_amount: Number(payAmount),
          payment_account_id: Number(payAccountId),
        })
      );
      if (res?.result) {
        toast.success("Payment recorded and posted to the ledger.");
        setShowPayModal(false);
        fetchPayables();
      } else {
        toast.error(res?.message || "Payment failed.");
      }
    } catch (err) {
      toast.error("Error recording payment.");
    } finally {
      setPaying(false);
    }
  };

  const statusLabel = (s) =>
    ({
      paid: "Paid",
      partial_paid: "Partially Paid",
      overdue: "Overdue",
      open: "Open",
    }[String(s || "open").toLowerCase()] || "Open");

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <FinanceBreadcrumb items={[{ label: "Accounts Payable" }]} />
        <div className="special-header panel-white-all">
          <h4 className="page-title">Accounts Payable</h4>
        </div>

        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Total Outstanding</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
          <div className="lib-tile lib-tile-overdue">
            <span className="lib-tile-label">Overdue</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.overdue)}
            </span>
          </div>
        </div>

        <div className="lib-filter-bar">
          <Form.Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="partial_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search by name"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button
            variant="secondary"
            onClick={() => setFilters({ status: "", search: "" })}
          >
            Clear
          </Button>
        </div>

        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Liability</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No payables found.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const amt = parseFloat(r.liability_amount) || 0;
                  const paid = parseFloat(r.liability_amount_paid) || 0;
                  const bal = amt - paid;
                  return (
                    <tr key={r.liability_id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="fw-semibold">{r.liability_name}</div>
                        {r.liability_description && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {r.liability_description}
                          </div>
                        )}
                      </td>
                      <td>
                        {r.liability_due_date
                          ? new Date(r.liability_due_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(amt)}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(paid)}
                      </td>
                      <td className="fw-bold">
                        {customer_currency} {formatNumber(bal)}
                      </td>
                      <td>{statusLabel(r.liability_status)}</td>
                      <td className="text-center">
                        {bal > 0 && (
                          <Button
                            size="sm"
                            onClick={() => openPayModal(r)}
                            style={{
                              backgroundColor: "var(--color-primary)",
                              border: "none",
                            }}
                          >
                            Record Payment
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      <Modal
        show={showPayModal}
        onHide={() => !paying && setShowPayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeRow && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div>
                <b>{activeRow.liability_name}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Balance {customer_currency} {formatNumber(balanceOf(activeRow))}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              Payment Amount <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Paid From (Cash / Bank) <span className="text-danger">*</span>
            </Form.Label>
            <AccountSelect
              accounts={accounts}
              types={["asset"]}
              value={payAccountId}
              onChange={(e) => setPayAccountId(e.target.value)}
              placeholder="Select cash / bank account"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPayModal(false)}
            disabled={paying}
          >
            Cancel
          </Button>
          <Button
            onClick={submitPayment}
            disabled={paying}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {paying ? <Spinner size="sm" /> : "Record Payment"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AccountsPayable;
